<template>
  <div
    :class="{
      'menu-list-item': true,
      'menu-list-item--selectable': selectable,
      'menu-list-item--selected': selectable && selected,
      'menu-list-item--has-dropdown': isDecorativeDropdown || dropdown,
      'menu-list-item--dropdown-collapsed': chevronValue,
      'menu-list-item--last': last,
    }"
    @click="onClick"
  >
    <div class="menu-list-item__content" @click="handleContentClick">
      <ui-icon
        class="menu-list-item__icon"
        :class="{ 'menu-list-item__icon--warning': warning }"
        size="12"
        :color="color || 'inherit'"
        :icon="icon"
        v-if="icon"
      ></ui-icon>
      <slot name="content">
        <span class="text-capitalize" :style="{ color: color || 'inherit' }">{{ title }}</span>
      </slot>
    </div>

    <div v-if="dropdown" v-show="dropdownOpen" class="menu-list-item__dropdown-body">
      <slot></slot>
    </div>

    <template v-if="isDecorativeDropdown || dropdown">
      <ui-icon
        v-if="!chevronValue"
        class="menu-list-item__icon menu-list-item__icon__chevron"
        size="12"
        :color="color || 'inherit'"
        @click="handleContentClick"
        icon="fas fa-chevron-down"
      ></ui-icon>

      <ui-icon
        v-else
        class="menu-list-item__icon menu-list-item__icon__chevron"
        size="12"
        :color="color || 'inherit'"
        @click="handleContentClick"
        icon="fas fa-chevron-up"
      ></ui-icon>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    color?: string;
    title?: string;
    icon?: string;
    dropdown?: boolean;
    last?: boolean;
    modelValue?: boolean;
    value?: boolean;
    decorativeDropdown?: boolean;
    selectable?: boolean;
    selected?: boolean;
    warning?: boolean;
  }>(),
  {
    selectable: false,
    selected: false,
  },
);

const emit = defineEmits<{
  click: [];
  'update:modelValue': [value: boolean];
  input: [value: boolean];
}>();

const dropdownOpen = ref(false);

const externalValue = computed(() => props.modelValue ?? props.value);

const isDecorativeDropdown = computed(() => typeof props.decorativeDropdown === 'boolean');

const chevronValue = computed(() =>
  isDecorativeDropdown.value ? props.decorativeDropdown : dropdownOpen.value,
);

watch(
  externalValue,
  (value) => {
    if (value !== undefined && value !== dropdownOpen.value) {
      dropdownOpen.value = !!value;
    }
  },
  { immediate: true },
);

const setDropdownOpen = (value: boolean) => {
  dropdownOpen.value = value;
  emit('update:modelValue', value);
  emit('input', value);
};

const onClick = () => {
  emit('click');
};

const handleContentClick = () => {
  if (props.dropdown) {
    setDropdownOpen(!dropdownOpen.value);
  }
};
</script>

<style lang="scss">
  .menu-list-item {
    display: flex;
    flex-direction: row;
    min-height: 40px;
    border-bottom: 1px solid var(--color-mobile-menu-border);
    color: var(--color-gray20);
    flex-grow: 0;
    user-select: none;

    &--header {
      height: 54px;
      user-select: auto ;
    }

    &--last {
      border-bottom: none;
      border-top: 1px solid var(--color-mobile-menu-border);
    }

    &--has-dropdown {
      flex-direction: column;
      position: relative;
    }

    &__dropdown-body {
      & > .menu-list-item {
        border-bottom: none;
        background-color: var(--color-mobile-menu-bg);
      }
    }

    &--selectable {
      position: relative;
    }

    &--selected, 
    &--selectable:hover {
      &::before {
        content: "";
        position: absolute;
        width: 4px;
        height: 100%;
        background-color: var(--color-green);
      }
    }

    &__icon {
      margin-right: 5px;

      &.v-icon {
        color: inherit;
      }
      
      @at-root #{selector-append(&, & + '--warning')} { 
        color: var(--color-red);
      }

      &__chevron {
        position: absolute;
        right: 16px;
        top: 13px;
        font-weight: 900;
        font-size: 12px;
        cursor: pointer;
      }
    }

    &__content {
      padding: 10px 16px;
      display: flex;
      align-items: baseline;
      width: 100%;
      min-height: 40px;

      &:hover {
        cursor: pointer;
        color: var(--color-white);
      }
    }
  }
</style>
